import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { seriesColor, useChartTheme } from "@/features/charts/theme";
import { ChartTooltip } from "@/features/charts/tooltip";
import { useDateFnsLocale } from "@/features/utils/i18n";
import { ResponsiveLine } from "@nivo/line";
import { format as formatDate } from "date-fns";

export interface TimeseriesPoint {
  /** Bucket start, ms epoch. */
  x: number;
  /** Null = no measurement in that hour (the line breaks instead of dropping to zero). */
  y: number | null;
}

export interface TimeseriesSeries {
  id: string;
  /**
   * Position in the chart palette (`--chart-1…5` then three more tokens). Given
   * explicitly where a series must keep its colour from one chart to the next —
   * the same provider on the latency and the volume chart.
   */
  colorIndex?: number;
  data: TimeseriesPoint[];
}

/** How many series the palette tells apart; the pages keep their top N. */
export const MAX_SERIES = 8;

/**
 * Hour-by-hour line chart. Deliberately plain: several series, a shared tooltip on the
 * hovered hour, and gaps left as gaps — a missing hour is "no traffic", not "zero latency".
 */
export function TimeseriesChart({
  title,
  description,
  series,
  format,
  isLoading,
  height = 280,
}: {
  title: string;
  description?: string;
  series: TimeseriesSeries[];
  format: (v: number) => string;
  isLoading?: boolean;
  height?: number;
}) {
  const hasData = series.some((s) => s.data.some((p) => p.y !== null));
  const { theme, tokens } = useChartTheme();
  const locale = useDateFnsLocale();
  const colors = series.map((s, index) =>
    seriesColor(tokens, s.colorIndex ?? index),
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </CardHeader>
      <CardContent>
        <div style={{ height }}>
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : !hasData ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Aucune donnée sur la période.
            </div>
          ) : (
            <ResponsiveLine
              data={series.map((s) => ({
                id: s.id,
                data: s.data.map((p) => ({ x: new Date(p.x), y: p.y })),
              }))}
              margin={{ top: 10, right: 20, bottom: 56, left: 55 }}
              xScale={{ type: "time", useUTC: false }}
              yScale={{ type: "linear", min: 0, max: "auto" }}
              curve="monotoneX"
              colors={colors}
              lineWidth={2}
              pointSize={0}
              enableGridX={false}
              gridYValues={5}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 0,
                tickPadding: 8,
                tickRotation: -35,
                format: (v: Date) =>
                  formatDate(v, "d MMM HH'h'", { locale }),
                tickValues: 8,
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 8,
                tickValues: 5,
                format: (v: number) => format(v),
              }}
              enableSlices="x"
              enableArea={false}
              animate={false}
              theme={theme}
              sliceTooltip={({ slice }) => (
                <ChartTooltip
                  title={
                    slice.points[0]
                      ? formatDate(
                          slice.points[0].data.x as Date,
                          "EEEE d MMM, HH:mm",
                          { locale },
                        )
                      : null
                  }
                  rows={slice.points.map((point) => ({
                    key: point.id,
                    color: point.seriesColor,
                    label: point.seriesId,
                    value: format(Number(point.data.y)),
                  }))}
                />
              )}
            />
          )}
        </div>
        {/* The legend is HTML rather than nivo's: nivo gives every entry one fixed
            width on a single row, so long model ids ran into each other and a
            half-width card clipped the entries at both ends. This one wraps. */}
        {!isLoading && hasData ? (
          <ul className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {series.map((s, index) => (
              <li key={s.id} className="flex min-w-0 items-center gap-1.5">
                <span
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: colors[index] }}
                />
                <span className="truncate">{s.id}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
